"use client"

import { useEffect, type RefObject } from "react"

interface ScrollScrubOptions {
  /**
   * Viewport-height fraction of the element's top where progress starts.
   * 0.92 → the scrub begins when the element top crosses 92% of the viewport
   * (i.e. just after it enters from the bottom).
   */
  startVh?: number
  /** Viewport-height fraction of the element's top where progress completes. */
  endVh?: number
  /**
   * EGDE-625: finish the scrub the moment the element is wholly inside the
   * viewport, instead of waiting for its top to climb to `endVh`. A window that
   * only completes near the top of the screen leaves the element mid-entrance
   * for the whole time it sits low in the viewport — QA read the content
   * banner's copy, parked part-way through its run-in, as "the card is only
   * fully visible in the top position".
   *
   * `endVh` stays the floor: an element taller than the viewport can never be
   * wholly inside it, so it keeps the fraction-based window.
   */
  completeWhenFullyVisible?: boolean
  /** Skip attaching listeners entirely (CMS animation toggle off). */
  disabled?: boolean
  /**
   * EGDE-477: park at the resting state (progress 1) when the element is
   * already inside the scrub window on mount — e.g. the vehicle-fleet gallery
   * sits at the top of its own page, where the first few scrolled pixels (the
   * same ones that swap the header for the floating pill) would otherwise
   * visibly displace it sideways. The entrance still plays wherever the
   * element starts below the viewport (homepage).
   */
  skipIfInitiallyVisible?: boolean
  /**
   * Progress reported when the visitor prefers reduced motion: 1 parks the
   * element in its final resting state (cards), 0 keeps a motion layer
   * un-displaced (marquee offset).
   */
  reducedMotionValue?: number
  /** Called with progress 0..1. Must be referentially stable (useCallback). */
  onProgress: (p: number) => void
}

/**
 * Scroll-scrubbed progress for parallax behaviors (EGDE-318).
 *
 * Maps the element's viewport position to a 0..1 progress on every scroll /
 * resize frame — scrolling back down-to-up reverses the progress, which is the
 * ticket's core requirement (the animation follows the scroll, it doesn't
 * play once). rAF-throttled like the ValuePropositions serpentine (EGDE-296),
 * and reports via callback so consumers can write CSS custom properties on a
 * ref without re-rendering per frame.
 */
export function useScrollScrub(
  ref: RefObject<HTMLElement | null>,
  {
    startVh = 0.92,
    endVh = 0.42,
    completeWhenFullyVisible = false,
    disabled = false,
    skipIfInitiallyVisible = false,
    reducedMotionValue = 1,
    onProgress,
  }: ScrollScrubOptions,
): void {
  useEffect(() => {
    if (disabled) return
    const el = ref.current
    if (!el) return

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onProgress(reducedMotionValue)
      return
    }

    if (skipIfInitiallyVisible) {
      const vh = window.innerHeight || document.documentElement.clientHeight
      if (el.getBoundingClientRect().top < vh * startVh) {
        onProgress(1)
        return
      }
    }

    /* EGDE-529: the raw position→progress mapping followed the scroll wheel
       1:1, so a quick flick snapped the animations through their whole range
       in a couple of frames — QA read them as "too quick / not matching
       design" next to the Figma prototype's eased transitions. The target
       progress still tracks the scroll position exactly (scrubbing back up
       reverses as before), but the REPORTED progress now eases toward the
       target with an exponential smoothing (~200ms time constant, ≈95% in
       0.6s), so fast scrolls play out as a short smooth settle. */
    const SMOOTH_TAU_MS = 200

    let raf = 0
    let target = 0
    let current: number | null = null
    let lastTick = 0

    const computeTarget = (): number => {
      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight || document.documentElement.clientHeight
      const start = vh * startVh
      /* EGDE-479: an element near the document bottom may never scroll its
         top up to endVh — the page runs out of scroll first and the progress
         stalls below 1 (the promotional banner stayed faded forever). Clamp
         the window's end to the lowest top the element can actually reach so
         the scrub always completes by max scroll. If even startVh is
         unreachable the element can only ever peek over the fold — park it
         at its resting state instead of keeping it invisible. */
      const remainingScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.scrollY - vh,
      )
      const minReachableTop = rect.top - remainingScroll
      if (minReachableTop >= start) return 1
      /* EGDE-625: the element's top when its bottom just clears the viewport
         floor. Above `vh * endVh` whenever the element fits on screen, so
         Math.max picks it up and lets go of the element there. */
      const fullyVisibleTop = completeWhenFullyVisible
        ? vh - rect.height
        : -Infinity
      const end = Math.max(vh * endVh, fullyVisibleTop, minReachableTop)
      const denom = Math.max(start - end, 1)
      return Math.min(1, Math.max(0, (start - rect.top) / denom))
    }

    const tick = (now: number) => {
      raf = 0
      target = computeTarget()
      if (current === null) {
        // First measurement (mount / restored scroll position): report the
        // real progress immediately — pages opened mid-scroll must not play
        // a catch-up entrance.
        current = target
        onProgress(current)
        return
      }
      const dt = Math.max(0, now - lastTick)
      lastTick = now
      const alpha = 1 - Math.exp(-dt / SMOOTH_TAU_MS)
      current += (target - current) * alpha
      if (Math.abs(target - current) < 0.001) {
        current = target
        onProgress(current)
        return
      }
      onProgress(current)
      raf = requestAnimationFrame(tick)
    }

    const schedule = () => {
      if (!raf) {
        // (Re)starting the settle loop after idle — reset the clock so the
        // first frame's dt is one frame, not the whole idle gap (a stale
        // lastTick would make alpha≈1 and snap straight to the target).
        lastTick = performance.now()
        raf = requestAnimationFrame(tick)
      }
    }

    schedule()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [
    ref,
    startVh,
    endVh,
    completeWhenFullyVisible,
    disabled,
    skipIfInitiallyVisible,
    reducedMotionValue,
    onProgress,
  ])
}
